import moment from 'moment/moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { Button, Form, Row, Col, Image } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IoIosLock, IoIosSkipBackward } from 'react-icons/io';

import SingleDatePicker from '../../../components/form/SingleDatePicker';

import { timeFormat } from '../../../constants';

import { getDateTimeMoment } from '../../../lib/utils';

import EquipmentPageLayout from '../components/EquipmentPageLayout';
import Title from '../../../components/Title';
import useDeviceSettings from '../hooks/useDeviceSettings';
import { getDevice } from '../selectors';
import { isFitPower } from '../utils';

// HTML
export function Heading1(key, label, description) {
  return (
    <div key={key}>
      <h4>{label}</h4>
      {description && <p className="text-muted">{description}</p>}
    </div>
  );
}
export function Heading2(key, label, description) {
  return (
    <div key={key}>
      <h6>{label}</h6>
      {description && <p className="text-muted">{description}</p>}
    </div>
  );
}
export function Paragraph(key, description) {
  return (
    <div key={key}>
      {description && <p className="text-muted">{description}</p>}
    </div>
  );
}
export function NewSection() {
  return <div className="border-bottom border-secondary mb-3" />;
}
export function PublicImage(value) {
  return (
    <div className="position-relative">
      {/*
        value.src must resolve to file in public directory e.g.
        src="/fitmachine-orientation.png" is "public/fitmachine-orientation.png"
      */}
      <Image src={value.src} alt={value.alt} fluid />
    </div>
  );
}
// Form
export function FormLabel(label) {
  return <Form.Label column sm="3">
    {label}
  </Form.Label>;
}
export function FormText(description) {
  return <Form.Text
    className="text-muted">
    {description}
  </Form.Text>;
}
export function FormCheck(register, key, disabled = false) {
  return <Form.Check
    type="checkbox"
    {...register(key)}
    disabled={disabled}
  />;
}
export function FormControlNumber(register, key, disabled = false) {
  // Number() causes a namespace clash with explosions and stuff. Pretty cool, but no.
  return <Form.Control
    type="number"
    step="any"
    {...register(key, { valueAsNumber: true })}
    style={{ width: '25%', minWidth: '8em' }}
    disabled={disabled}
  />;
}
export function FormControlDropdown(register, key, value, disabled = false) {
  return <Form.Control
    as="select"
    defaultValue={''}
    {...register(key)}
    style={{ width: '50%', minWidth: '8em' }}
    disabled={disabled}
  >
    {value.map((item) => <option key={item} value={item}>{item}</option>)}
  </Form.Control>;
}
export function FormControlText(register, key, disabled = false) {
  return <Form.Control
    type="text"
    {...register(key)}
    disabled={disabled}
  />;
}

function EquipmentSettings() {
  const { t } = useTranslation();
  const { id } = useParams();
  const deviceId = Number(id);
  const device = useSelector(state => getDevice(state, deviceId));
  const initialMoment = moment(undefined);
  const [valid, setValid] = useState(true);
  const [chooseDate, setChooseDate] = useState(false);
  const [state, setState] = useState(() => {
    return {
      // use absolute start of days
      dateMoment: initialMoment.clone().startOf('day'),
      // need to be able to keep invalid dates in the field
      timeString: initialMoment.format(timeFormat),
    };
  });
  const {
    loading,
    lastFetch,
    error,
    settings = {},
    updateSettings,
    lockSettings,
  } = useDeviceSettings(deviceId, { fetch: true });

  const setDropdown = (values) => {
    // Populate dropdowns with stored value (if any) from API or set to first entry in option array.
    const newObj = {};
    Object.entries(values).forEach(([key, value]) => {
      if (key.includes('_dropdown')) {
        // In the API the related fields must have the same prepended before dropdown and value,
        // e.g. dg_asset_drive_type_dropdown and dg_asset_drive_type_value.
        const dropdownName = key.split('_dropdown')[0];
        const newKey = `${dropdownName}_value`;
        const setValue = values[newKey];
        if (setValue !== '') newObj[key] = setValue;
        else newObj[key] = value[0]; // Set default selected
      } else {
        newObj[key] = value;
      }
    });
    return newObj;
  };

  const parseDropdown = (data) => {
    // Parse selected from dropdown to set the actual value to be stored in the database. The
    // field e.g. dg_asset_drive_type_dropdown stores the options, whereas
    // e.g. dg_asset_drive_type_value stores the customer's option choice.
    const newObj = {};
    Object.entries(data).forEach(([key, value]) => {
      if (key.includes('_dropdown')) {
        const dropdownName = key.split('_dropdown')[0];
        const newKey = `${dropdownName}_value`;
        newObj[newKey] = value;
      } else {
        // Otherwise we'll overwrite the newly created one as the API has the _value as a hidden field
        if (!(key in newObj)) {
          newObj[key] = value;
        }
      }
    });
    return newObj;
  };

  const defaultValues = useMemo(() => {
    const values = {};
    for(const key of Object.keys(settings)) {
      values[key] = settings[key].value;
    }
    return setDropdown(values);
  }, [settings]);

  const { register, handleSubmit, reset } = useForm({
    mode: 'onSubmit',
    defaultValues
  });

  useEffect(() => {
    if(defaultValues && Object.keys(defaultValues).length > 0) {
      reset(defaultValues);
    }
  }, [defaultValues]);

  const onSubmit = (data) => {
    data = parseDropdown(data);
    updateSettings(data);
  };

  const onLockClick = () => {
    const dateTimeMoment = getDateTimeMoment(state.dateMoment, state.timeString);
    const validTo = dateTimeMoment ? dateTimeMoment.toISOString() : null;
    lockSettings(validTo);
  };

  return (
    <EquipmentPageLayout>
      <div className="my-4 mx-3">
        <Title
          title={t('screens.equipment.equipment-settings.title')}
          loading={loading}
          lastFetch={lastFetch}
          error={error}
        />
        {isFitPower(device) && <div>
          <p>{t('screens.equipment.equipment-settings.snapshot-current-settings')}</p>
          <Form>
            <div>
              <Form.Group as={Row} controlId={'previous-settings-form'}>
                <Form.Label column sm="3">
                  {t('screens.equipment.equipment-settings.backdate-changes')}
                </Form.Label>
                <Col sm="9">
                  <Form.Check
                    type="checkbox"
                    checked={chooseDate}
                    onChange={() => setChooseDate(!chooseDate)}
                  />
                  {FormText(t('screens.equipment.equipment-settings.when-checked'))}
                  <Col>
                    {chooseDate && <>
                      <Row>
                        <SingleDatePicker
                          key={`previous-settings-${state.dateMoment.toISOString()}`}
                          date={state.dateMoment}
                          onDateChange={dateMoment => {
                            const {timeString} = state;
                            const newDateTime = getDateTimeMoment(dateMoment, timeString);
                            setState({
                              ...state,
                              dateMoment: dateMoment.clone().startOf('day'),
                            });
                            setValid(newDateTime && !newDateTime.isAfter());
                          }}
                          showDefaultInputIcon={true}
                          id="previous-settings-date"
                        />
                        &nbsp;
                        <Form.Control
                          type="text"
                          style={{
                            height: 48,
                            width: '170px',
                          }}
                          name="previous-settings-time"
                          isInvalid={!valid}
                          value={state.timeString}
                          onChange={e => {
                            const {dateMoment} = state;
                            const timeString = e.target.value;
                            const dateTimeMoment = getDateTimeMoment(dateMoment, timeString);
                            setState({...state, timeString});
                            setValid(dateTimeMoment && !dateTimeMoment.isAfter());
                          }}
                        />
                      </Row>
                    </>}
                    &nbsp;
                    <Row>
                      <Button
                        variant="outline-dark"
                        type="submit"
                        size="md"
                        disabled={loading}
                        onClick={onLockClick}
                      ><IoIosLock /> <span>{t('screens.equipment.equipment-settings.lock-current-settings')}</span>
                      </Button>
                      &nbsp;
                      <Link to={`/equipment/${device.id}/settings/previous`}>
                        <Button
                          variant="outline-dark"
                          type="submit"
                          size="md"
                          disabled={loading}
                        ><IoIosSkipBackward /> <span>{t('screens.equipment.equipment-settings.view-previous-settings')}</span></Button>
                      </Link>
                    </Row>
                  </Col>
                </Col>
              </Form.Group>
            </div>
          </Form>
          {NewSection()}
        </div>}
        <Form onSubmit={handleSubmit(onSubmit)}>
          {
            Object.keys(settings).map(key => {
              const {label, description, value_type, value, new_section, disabled } = settings[key];
              if (value_type === 'heading1') {
                return Heading1(key, label, description);
              }
              if (value_type === 'heading2') {
                return Heading2(key, label, description);
              }
              if (value_type === 'paragraph') {
                return Paragraph(key, description);
              }
              return (
                <div key={key}>
                  {new_section && NewSection()}
                  <Form.Group as={Row} controlId={key}>
                    {FormLabel(label)}
                    <Col sm="9">
                      {value_type === 'bool' ? FormCheck(register, key) :
                        value_type === 'number' ? FormControlNumber(register, key, disabled) :
                          value_type === 'image' ? PublicImage(value) :
                            value_type === 'dropdown' ? FormControlDropdown(register, key, value) :
                              value_type === 'hidden' ? null :
                                FormControlText(register, key)
                      }
                      {FormText(description)}
                    </Col>
                  </Form.Group>
                </div>
              );
            })
          }
          <Button
            variant="success"
            type="submit"
            size="lg"
            className="float-right"
            disabled={loading}
          >{t('common.save')}</Button>
        </Form>
      </div>
    </EquipmentPageLayout>
  );
}

export default EquipmentSettings;
